import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logError } from "@/lib/errorLogger";

// Create a reschedule proposal for an existing scheduled meeting
export function useRescheduleProposal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      meetingId,
      threadId,
      durationMinutes,
      timezone,
      note,
      slots,
    }: {
      meetingId: string;
      threadId: string;
      durationMinutes: number;
      timezone: string;
      note?: string;
      slots: { start_at: string; end_at: string }[];
    }): Promise<{ meeting_id: string; proposal_id: string; slots_inserted: number }> => {
      if (!user?.id) {
        throw new Error("Not authenticated");
      }

      if (!slots || slots.length === 0) {
        throw new Error("At least one time slot is required");
      }

      console.log("[useRescheduleProposal] Calling RPC with:", {
        p_meeting_id: meetingId,
        p_thread_id: threadId,
        p_duration_minutes: durationMinutes,
        p_timezone: timezone,
        p_slots_count: slots.length,
      });

      const { data, error } = await supabase.rpc("create_reschedule_proposal", {
        p_meeting_id: meetingId,
        p_thread_id: threadId,
        p_duration_minutes: durationMinutes,
        p_timezone: timezone,
        p_note: note || null,
        p_slots: slots,
      });

      if (error) {
        console.error("[useRescheduleProposal] RPC error:", error);
        throw new Error(`${error.code || "RPC_ERROR"}: ${error.message}`);
      }

      console.log("[useRescheduleProposal] RPC returned:", data);
      
      const responseData = data as { meeting_id?: string; proposal_id?: string; slots_inserted?: number } | null;
      
      return {
        meeting_id: responseData?.meeting_id || meetingId,
        proposal_id: responseData?.proposal_id || "",
        slots_inserted: responseData?.slots_inserted || 0,
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
    },
    onError: (error) => {
      console.error("[useRescheduleProposal] Mutation error:", error);
    },
  });
}

// Accept a reschedule proposal slot - handles Zoom delete/create
export function useAcceptRescheduleSlot() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slotId,
      proposalId,
      threadId,
      duration,
      meetingId,
      oldZoomMeetingId,
    }: {
      slotId: string;
      proposalId: string;
      threadId: string;
      duration: number;
      meetingId: string;
      oldZoomMeetingId?: string;
    }) => {
      if (!user?.id) {
        throw new Error("Not authenticated");
      }

      console.log("[useAcceptRescheduleSlot] Starting reschedule accept:", {
        slotId,
        meetingId,
        oldZoomMeetingId,
      });

      // Step 1: Delete old Zoom meeting if exists (non-blocking)
      if (oldZoomMeetingId) {
        try {
          console.log("[useAcceptRescheduleSlot] Deleting old Zoom meeting:", oldZoomMeetingId);
          const deleteResponse = await supabase.functions.invoke("delete-zoom-meeting", {
            body: { meeting_id: oldZoomMeetingId },
          });
          if (deleteResponse.error) {
            console.error("[useAcceptRescheduleSlot] Failed to delete old Zoom:", deleteResponse.error);
            // Log but don't fail - continue with reschedule
            await logError({
              error: `Failed to delete old Zoom meeting: ${deleteResponse.error.message}`,
              component: "useAcceptRescheduleSlot",
              action: "delete_old_zoom",
              metadata: { meetingId, oldZoomMeetingId },
            });
          } else {
            console.log("[useAcceptRescheduleSlot] Old Zoom meeting deleted successfully");
          }
        } catch (err) {
          console.error("[useAcceptRescheduleSlot] Error calling delete-zoom-meeting:", err);
          // Continue anyway
        }
      }

      // Step 2: Create new Zoom meeting with slot_id as source of truth
      console.log("[useAcceptRescheduleSlot] Creating new Zoom meeting for slot:", slotId);
      const zoomResponse = await supabase.functions.invoke("create-zoom-meeting", {
        body: {
          duration,
          slot_id: slotId,
          meeting_id: meetingId,
        },
      });

      if (zoomResponse.error) {
        console.error("[useAcceptRescheduleSlot] Zoom creation failed:", zoomResponse.error);
        throw new Error("Failed to create new Zoom meeting. Please try again.");
      }

      console.log("[useAcceptRescheduleSlot] New Zoom meeting created:", {
        zoom_meeting_id: zoomResponse.data?.meeting_id,
        resolved_start_at: zoomResponse.data?.resolved_start_at,
      });

      // Step 3: Accept the reschedule slot (updates meeting, clears reschedule state)
      const { data: acceptedMeetingId, error } = await supabase.rpc("accept_reschedule_slot", {
        p_slot_id: slotId,
        p_zoom_meeting_id: zoomResponse.data?.meeting_id || null,
        p_zoom_join_url: zoomResponse.data?.join_url || null,
        p_zoom_start_url: zoomResponse.data?.start_url || null,
      });

      if (error) {
        console.error("[useAcceptRescheduleSlot] accept_reschedule_slot RPC failed:", error);
        throw error;
      }

      console.log("[useAcceptRescheduleSlot] Reschedule accepted, meetingId:", acceptedMeetingId);

      // Step 4: Send updated calendar invite
      if (acceptedMeetingId && typeof acceptedMeetingId === "string") {
        const resolvedStartAt = zoomResponse.data?.resolved_start_at;
        
        if (resolvedStartAt) {
          const scheduledStart = new Date(resolvedStartAt);
          const now = new Date();
          const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

          if (scheduledStart >= fiveMinutesFromNow) {
            console.log("[useAcceptRescheduleSlot] Sending updated calendar invite");
            
            // Fire-and-forget: Don't await - prevents blocking the accept flow
            supabase.functions.invoke("send-calendar-invite", {
              body: { meetingId: acceptedMeetingId, action: "update" },
            }).then(inviteResponse => {
              if (inviteResponse.error) {
                console.error("[useAcceptRescheduleSlot] Calendar invite error:", inviteResponse.error);
              } else {
                console.log("[useAcceptRescheduleSlot] Updated calendar invite sent");
              }
            }).catch(inviteErr => {
              console.error("[useAcceptRescheduleSlot] Calendar invite exception:", inviteErr);
            });
          }
        }
      }

      return acceptedMeetingId;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["pending-meeting-proposal", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      toast.success("Meeting rescheduled! New Zoom link created.");
    },
    onError: (error) => {
      toast.error("Failed to accept reschedule: " + error.message);
    },
  });
}

// Decline a reschedule proposal - keeps original meeting intact
export function useDeclineRescheduleProposal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      proposalId,
      meetingId,
      threadId,
      reason,
    }: {
      proposalId: string;
      meetingId: string;
      threadId: string;
      reason?: string;
    }) => {
      if (!user?.id) {
        throw new Error("Not authenticated");
      }

      console.log("[useDeclineRescheduleProposal] Declining proposal:", proposalId);

      const { data, error } = await supabase.rpc("decline_reschedule_proposal", {
        p_proposal_id: proposalId,
        p_thread_id: threadId,
        p_reason: reason || null,
      });

      if (error) {
        console.error("[useDeclineRescheduleProposal] RPC error:", error);
        throw error;
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      toast.success("Reschedule proposal declined. Original meeting remains.");
    },
    onError: (error) => {
      toast.error("Failed to decline reschedule: " + error.message);
    },
  });
}
